import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import hero from "../../images/hero1.jpg";
import { cities } from "../../data/citiesData";

// Pakistan major cities for ride sharing
const pakistanCities = [
  "Karachi", "Lahore", "Islamabad", "Rawalpindi", "Faisalabad",
  "Multan", "Peshawar", "Quetta", "Gujranwala", "Sialkot",
  "Hyderabad", "Abbottabad", "Bahawalpur", "Sargodha", "Sukkur", "Swat", "Gilgit", "Skardu", "Muzaffarabad", "Mardan", "Taxila", "Dir", "Chitral", "Nowshehra", "Swabi", "Malakand"
];

const HeroSection = () => {
  const [carModel, setCarModel] = useState("");
  const [city, setCity] = useState("");
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [listingType, setListingType] = useState("sale");
  const [rideType, setRideType] = useState("one-way");

  const navigate = useNavigate();

  const handleSearch = () => {
    let params = { carModel };

    if (listingType === "ride") {
      if (!fromCity || !toCity) {
        alert("Please select both departure and destination cities");
        return;
      }
      params = { 
        ...params, 
        fromCity, 
        toCity,
        rideType 
      };
    } else {
      if (!carModel) {
        alert("Please enter a car model");
        return;
      }
      params.city = city;
    }

    switch (listingType) {
      case "sale":
        navigate("/category/sale", { state: params });
        break;
      case "rent":
        navigate("/category/rent", { state: params });
        break;
      case "ride":
        navigate("/category/ride", { state: params });
        break;
      default:
        navigate("/category/sale", { state: params });
    }
  };

  const listingTypeButtons = [
    { id: "sale", label: "For Sale", color: "from-blue-400 to-blue-600", icon: "🚗" },
    { id: "rent", label: "For Rent", color: "from-cyan-400 to-blue-500", icon: "🔑" },
    { id: "ride", label: "Ride Sharing", color: "from-indigo-400 to-blue-600", icon: "👥" },
  ];

  return (
    <div
      className="relative text-white flex flex-col items-center justify-center min-h-[85vh] px-4 py-12 text-center overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.4)), url(${hero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Animated gradient overlays */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-blue-400/15 to-cyan-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-indigo-400/10 to-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-300/5 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl">
        {/* Main Heading */}
        <div className="mb-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 bg-gradient-to-r from-blue-300 via-cyan-300 to-indigo-300 bg-clip-text text-transparent drop-shadow-2xl tracking-tight">
            Find Your Perfect Vehicle
          </h1>
          <p className="text-xl md:text-2xl text-gray-100 font-light max-w-3xl mx-auto">
            Discover cars for sale, rent, or ride sharing across Pakistan. Your journey starts here with NCPWheels.
          </p>
        </div>

        {/* Listing Type Buttons */}
        <div className="mb-8 flex flex-col items-center">
          <p className="text-gray-200 mb-4 font-medium">What are you looking for?</p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {listingTypeButtons.map((type) => (
              <button
                key={type.id}
                onClick={() => {
                  setListingType(type.id);
                  // Reset ride-specific fields when switching from ride
                  if (type.id !== "ride") {
                    setFromCity("");
                    setToCity("");
                  }
                }}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${
                  listingType === type.id
                    ? `bg-gradient-to-r ${type.color} text-white shadow-lg border-2 border-white/40`
                    : "bg-white/15 backdrop-blur-sm text-white border border-white/30 hover:bg-white/25"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>

          {/* Active indicator */}
          <div className="w-24 h-1 bg-gradient-to-r from-blue-300 via-cyan-300 to-indigo-300 rounded-full mb-2"></div>
          <span className="text-sm text-gray-200 font-medium capitalize">{listingType} Listings</span>
        </div>

        {/* Search Container */}
        <div className="bg-gradient-to-br from-white/15 to-white/8 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/30 shadow-2xl w-full max-w-4xl mx-auto mb-10">
          <div className="flex flex-col gap-4 w-full">
            
            {/* Car Model Input - Always visible */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-200 mb-2 text-left">
                Car Model {listingType !== "ride" && <span className="text-red-300">*</span>}
              </label>
              <input
                type="text"
                placeholder={
                  listingType === "ride" 
                    ? "e.g. Toyota Corolla, Honda Civic (optional)" 
                    : "e.g. Toyota Corolla, Honda Civic, Suzuki Mehran..."
                }
                className="w-full px-5 py-4 rounded-xl bg-white/95 text-gray-800 placeholder-gray-400 border-2 border-white/40 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/40"
                value={carModel}
                onChange={(e) => setCarModel(e.target.value)}
              />
              {listingType === "ride" && (
                <p className="text-xs text-gray-300 mt-1 text-left">Optional: Specify car model for your ride</p>
              )}
            </div>

            {/* Ride Sharing Specific Fields */}
            {listingType === "ride" && (
              <div className="space-y-4">
                {/* Ride Type Selection */}
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => setRideType("one-way")}
                    className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                      rideType === "one-way"
                        ? "bg-blue-500 text-white"
                        : "bg-white/20 text-gray-200 hover:bg-white/30"
                    }`}
                  >
                    One Way
                  </button>
                  <button
                    onClick={() => setRideType("round-trip")}
                    className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                      rideType === "round-trip"
                        ? "bg-blue-500 text-white"
                        : "bg-white/20 text-gray-200 hover:bg-white/30"
                    }`}
                  >
                    Round Trip
                  </button>
                </div>

                {/* From and To Cities */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-200 mb-2 text-left">
                      From City <span className="text-red-300">*</span>
                    </label>
                    <select
                      className="w-full px-5 py-4 rounded-xl bg-white/95 text-gray-800 border-2 border-white/40 shadow-lg transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/40 appearance-none cursor-pointer"
                      value={fromCity}
                      onChange={(e) => setFromCity(e.target.value)}
                    >
                      <option value="">Select departure city</option>
                      {pakistanCities.map((cityName) => (
                        <option key={cityName} value={cityName}>
                          {cityName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-blue-500/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-200 mb-2 text-left">
                      To City <span className="text-red-300">*</span>
                    </label>
                    <select
                      className="w-full px-5 py-4 rounded-xl bg-white/95 text-gray-800 border-2 border-white/40 shadow-lg transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/40 appearance-none cursor-pointer"
                      value={toCity}
                      onChange={(e) => setToCity(e.target.value)}
                    >
                      <option value="">Select destination city</option>
                      {pakistanCities.map((cityName) => (
                        <option key={cityName} value={cityName}>
                          {cityName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Swap Cities Button */}
                {fromCity && toCity && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        setFromCity(toCity);
                        setToCity(fromCity);
                      }}
                      className="text-sm bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-all duration-200 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                      Swap Cities
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Location Select - For Sale/Rent */}
            {listingType !== "ride" && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-200 mb-2 text-left">
                  Location
                </label>
                <select
                  className="w-full px-5 py-4 rounded-xl bg-white/95 text-gray-800 border-2 border-white/40 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/40 appearance-none cursor-pointer"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                >
                  <option value="">All Cities in Pakistan</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.city}>
                      {city.city}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Search Button */}
            <div className="flex justify-center mt-4">
              <button
                onClick={handleSearch}
                className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-xl border-2 border-white/40 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center justify-center gap-2 group"
              >
                <svg className="w-5 h-5 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {listingType === "ride" ? "Find Rides" : "Search Vehicles"}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <button
            onClick={() => navigate("/featured-sellers")}
            className="px-5 py-3 bg-gradient-to-r from-blue-500/25 to-indigo-600/25 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 flex items-center gap-2 group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Browse Showrooms
          </button>
          <button
            onClick={() => navigate("/category/sale")}
            className="px-5 py-3 bg-gradient-to-r from-blue-500/25 to-cyan-600/25 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 flex items-center gap-2 group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Browse All Cars
          </button>
          <button
            onClick={() => navigate("/category/rent")}
            className="px-5 py-3 bg-gradient-to-r from-indigo-500/25 to-blue-600/25 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 flex items-center gap-2 group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            Browse Rentals
          </button>
          <button
            onClick={() => navigate("/category/ride")}
            className="px-5 py-3 bg-gradient-to-r from-indigo-500/25 to-blue-600/25 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 flex items-center gap-2 group"
          >
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            Browse Rides
          </button>
        </div>

        {/* Stats */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <p className="text-gray-200 text-sm font-medium mb-4">Trusted by thousands of users across Pakistan</p>
          <div className="flex flex-wrap justify-center gap-8 text-gray-200">
            <div className="text-center group cursor-default">
              <div className="text-2xl font-bold text-white transition-all group-hover:text-blue-300">5,000+</div>
              <div className="text-sm">Vehicles Listed</div>
            </div>
            <div className="text-center group cursor-default">
              <div className="text-2xl font-bold text-white transition-all group-hover:text-cyan-300">200+</div>
              <div className="text-sm">Verified Sellers</div>
            </div>
            <div className="text-center group cursor-default">
              <div className="text-2xl font-bold text-white transition-all group-hover:text-indigo-300">15+</div>
              <div className="text-sm">Cities Covered</div>
            </div>
            <div className="text-center group cursor-default">
              <div className="text-2xl font-bold text-white transition-all group-hover:text-blue-300">500+</div>
              <div className="text-sm">Daily Rides</div>
            </div>
          </div>
        </div>

        {/* Featured Ride Routes - Only show when ride is selected but can be shown always */}
        {listingType === "ride" && (
          <div className="mt-8 pt-6">
            <p className="text-gray-300 text-sm mb-3">Popular Routes</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { from: "Karachi", to: "Lahore" },
                { from: "Lahore", to: "Islamabad" },
                { from: "Islamabad", to: "Rawalpindi" },
                { from: "Karachi", to: "Hyderabad" },
              ].map((route, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setFromCity(route.from);
                    setToCity(route.to);
                  }}
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm hover:bg-white/20 transition-all duration-200"
                >
                  {route.from} → {route.to}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroSection;